import React, { useState, useMemo } from 'react';
import { parse } from 'date-fns';
import { da } from 'date-fns/locale';
import { Anchor } from '~/shared/components/Anchor';
import Typography from '~/shared/components/Typography/Typography';
import ProfileHeader from '../ProfileHeader/ProfileHeader';
import ReceiptsList from '../../components/ReceiptsList/ReceiptsList';
import ReceiptsCardDrawer from '../ReceiptsCardDrawer/ReceiptsCardDrawer';
import { usePurchaseHistory } from '~/shared/hooks/loyalty/queries/usePurchaseHistory';
import { useReceipt } from '~/shared/hooks/loyalty/queries/useReceipt';
import { useTranslation } from '~/shared/utils/translation';
import { mapPurchaseToReceiptCard, mapReceiptToReceiptDetails } from '../../lib/loyaltyDataMappers';
import type { ReceiptCardData } from '~/lib/data-contract/loyalty';
import type { ReceiptDetails } from '~/lib/data-contract/loyalty';
import styles from './ProfileTabs.module.scss';

const HelpSection = () => (
    <>
        <Typography as="h3" variant="paragraph-M" weight="medium">
            Har du spørgsmål til dine køb?
        </Typography>
        <Typography as="p" variant="paragraph-S" weight="regular">
            Du kan altid ringe eller skrive til os eller henvende dig i din lokale Imerco, hvor
            vores personale står klar til at hjælpe dig.
        </Typography>
        <div className={styles.helpLinks}>
            <Anchor href="https://kundeservice.imerco.dk/s/" variant="paragraph-S" target="_blank">
                Kontakt kundeservice
            </Anchor>
            <Anchor
                href="https://kundeservice.imerco.dk/s/byt-og-retur"
                variant="paragraph-S"
                target="_blank"
            >
                Bytte og returre
            </Anchor>
            <Anchor
                href="https://kundeservice.imerco.dk/s/reklamation-og-garanti "
                variant="paragraph-S"
                target="_blank"
            >
                Reklamation og garanti
            </Anchor>
        </div>
    </>
);

const Purchases: React.FC = () => {
    const { translate } = useTranslation();
    const [selectedReceiptId, setSelectedReceiptId] = useState<string | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Fetch purchase history from API (lightweight list)
    const { data: purchaseData, isLoading, error } = usePurchaseHistory();

    // Fetch full receipt details when a receipt is selected (lazy loading)
    const { data: receiptData } = useReceipt(selectedReceiptId || '');

    // Map API purchases to receipt cards
    const receipts = useMemo<ReceiptCardData[]>(() => {
        if (!purchaseData?.value) return [];

        return purchaseData.value.map(mapPurchaseToReceiptCard).sort((a, b) => {
            // Sort by date descending (newest first)
            // Parse Danish month names using date-fns
            const dateStrA = `${a.date.day} ${a.date.month} ${a.date.year}`;
            const dateStrB = `${b.date.day} ${b.date.month} ${b.date.year}`;

            const dateA = parse(dateStrA, 'd MMMM yyyy', new Date(), { locale: da });
            const dateB = parse(dateStrB, 'd MMMM yyyy', new Date(), { locale: da });

            return dateB.getTime() - dateA.getTime();
        });
    }, [purchaseData]);

    // Map receipt data to details when available
    // API returns an array, take the first element
    const selectedReceipt = useMemo<ReceiptDetails | null>(() => {
        if (
            !receiptData?.value ||
            !Array.isArray(receiptData.value) ||
            receiptData.value.length === 0
        ) {
            return null;
        }
        const transaction = receiptData.value[0];
        return mapReceiptToReceiptDetails(transaction);
    }, [receiptData]);

    // Handle receipt click - set the ID to trigger lazy loading
    const handleReceiptClick = (id: string) => {
        setSelectedReceiptId(id);
        setDrawerOpen(true);
    };

    // Handle drawer close
    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        // Optionally clear selectedReceiptId when drawer closes to free memory
        // setSelectedReceiptId(null);
    };

    return (
        <div className={styles.tabContent}>
            <div className={styles.inner}>
                <ProfileHeader
                    title={translate('loyalty.purchases.title')}
                    subtitle={translate('loyalty.purchases.subtitle')}
                />
                <section className={styles.receiptsSection}>
                    <ReceiptsList
                        receipts={receipts}
                        title={translate('loyalty.purchases.receipts.title')}
                        description={translate('loyalty.purchases.receipts.description')}
                        onReceiptClick={handleReceiptClick}
                        helpSection={<HelpSection />}
                        isLoading={isLoading}
                        hasError={!!error}
                    />
                </section>

                {/* Receipt details drawer */}
                {drawerOpen && selectedReceipt && (
                    <ReceiptsCardDrawer
                        open={drawerOpen}
                        onClose={handleCloseDrawer}
                        receipt={selectedReceipt}
                        scrollLock={true}
                    />
                )}
            </div>
        </div>
    );
};

export default Purchases;
